import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddressBook, ArrowCounterClockwise, ArrowDownLeft, ArrowsLeftRight, ArrowUpRight, Briefcase, CalendarBlank, CalendarCheck, Check, Coins, CurrencyEur,
  DoorOpen, EnvelopeSimple, Export, FilePdf, FileText, HourglassMedium, Info, PaperPlaneTilt, Receipt, Scales, SealCheck, ShareFat, ShieldCheck,
  SignOut, User, Users, Bank, CheckCircle, TextAlignLeft, Wrench, X, XCircle, type Icon,
} from '@phosphor-icons/react';
import {
  FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, PanelZeile, SegmentedControl, Stepper, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT, BUCHHALTUNG_FOLGT_LANG } from '../../../../../lib/immobilien/hinweise';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { oeffneMieterDokument } from '../../../../../lib/mieter/mieterDokumente';
import { leseZahl } from '../../../../../lib/immobilien/mietverhaeltnisFormular';
import { zuordnungsDaten } from '../../../../../lib/eigentuemer/liste';
import { vertragsArt, vertragsDauer } from '../../../../../lib/eigentuemer/detail';
import { aktualisiereEigentuemer, entscheideFreigabe, erfasseFreigabe, loescheGeplanteVerguetung, speichereVerguetung } from '../../../../../lib/eigentuemer/aktionen';
import { meldeEigentuemerGeaendert } from '../../../../../lib/eigentuemer/useEigentuemerDetail';
import {
  abrechnungsZeilen, aktuelleVerguetung, anlageVZeilen, freigabeKennzahlen, fristAus, fristText, geplanteVerguetung, innerhalbWertgrenze, KUENDIGUNG_ZUM, kuendigungsTermin,
  mieterZeilen, naechsterMonatsanfang, umfangText, wegNamenMitSev, type FreigabeStatus, type KuendigungZum,
} from '../../../../../lib/eigentuemer/verwaltung';
import { DatenZeile } from '../../../objekte/einheit/bereiche/teile';
import { useEigSeite, type EigSeitenWert } from '../EigentuemerSeitenKontext';
import { AeltereEinsaetzeKarte } from './AeltereEinsaetzeKarte';
import { SteuerpaketEigentuemer } from '../../../../../components/buchhaltung/SteuerpaketEigentuemer';

const GLAS: React.CSSProperties = { background: 'var(--glass)' };
const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');
const t = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());
const zahl = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** Heute gültige Vergütung je Einheit (Verlauf, sonst gespeicherter Wert). */
const heutigeVerguetung = (s: EigSeitenWert): number | null => aktuelleVerguetung(s.daten.verguetungen, s.heute, zahl(s.daten.eigentuemer.verguetung_je_einheit));

/** Karte mit Titel und Datenzeilen (Prototyp-Block „rows“): zweispaltig ab 560 px. */
function Zeilen({ titel, icon, zeilen }: { titel: string; icon?: Icon; zeilen: { label: string; wert: React.ReactNode; zahl?: boolean }[] }) {
  return (
    <InnenKarte style={GLAS} gap={0}>
      <KartenTitel icon={icon}>{titel}</KartenTitel>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', columnGap: 28 }}>
        {zeilen.map(z => <DatenZeile key={z.label} label={z.label} wert={z.wert} zahl={z.zahl} />)}
      </div>
    </InnenKarte>
  );
}

function Hinweis({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <InnenKarte style={GLAS}>
      <div className="flex items-start" style={{ gap: 12 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--acc-soft)' }}>
          <Info weight="fill" size={18} color="var(--acc)" />
        </span>
        <div className="flex flex-col" style={{ gap: 4 }}>
          <span style={{ fontSize: 15, fontWeight: 800 }}>{titel}</span>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>{children}</span>
        </div>
      </div>
    </InnenKarte>
  );
}

// ── Eigentümerkonto (Screens 27, 28, 66) ───────────────────────────────────────
export function KontoPanel() {
  const s = useEigSeite();
  const z = s.daten.zeile;
  const w = z.werte;
  const eig = s.daten.eigentuemer;
  const p = s.portfolio;
  const zeilen = abrechnungsZeilen({
    w, mieterAnzahl: p.vermietet, wegNamen: wegNamenMitSev(z.zuordnungen),
    fremdverwalter: s.daten.fremdverwalter.map(f => f.name), verguetungJeEinheit: heutigeVerguetung(s),
    empfaenger: t(eig.kontoinhaber) || s.name,
  });
  const unterdeckung = w.ausz < 0;
  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" label="Saldo" wert="–" sub={BUCHHALTUNG_FOLGT} icon={Scales} ton="neutral" />
        <KpiTile layout="kachel" label="Mieteinnahmen / Monat" wert={eur(w.miete, { nachkomma: 0 })} icon={ArrowDownLeft} ton="ok" />
        <KpiTile layout="kachel" label={unterdeckung ? 'Unterdeckung / Monat' : 'Auszahlung / Monat'} wert={eur(w.ausz, { nachkomma: 0 })} icon={ArrowUpRight} ton={unterdeckung ? 'bad' : 'info'} wertFarbig={unterdeckung} />
      </KpiReihe>
      <InnenKarte style={GLAS} gap={0}>
        <KartenTitel>Monatliche Abrechnung</KartenTitel>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', columnGap: 28 }}>
          {zeilen.map(x => (
            <DatenZeile key={x.label} label={x.label} zahl wert={
              <span style={{ color: x.art === 'plus' ? 'var(--ok)' : x.art === 'auszahlung' ? (x.betrag < 0 ? 'var(--bad)' : 'var(--info)') : 'var(--ink)' }}>
                {x.art === 'plus' ? `+${eur(x.betrag)}` : x.art === 'minus' ? `−${eur(x.betrag)}` : eur(x.betrag)}
              </span>
            } />
          ))}
        </div>
        {w.verwaltet && heutigeVerguetung(s) === null && (
          <span style={{ marginTop: 10, fontSize: 12, color: 'var(--ink3)' }}>Ohne Verwaltervergütung gerechnet – die Vergütung je Einheit ist noch nicht hinterlegt (Verwaltervertrag).</span>
        )}
      </InnenKarte>
      {unterdeckung && (
        <Hinweis titel="Hinweis">Die Mieteinnahmen decken die Abzüge nicht; Hausgeld und Vergütung werden vorgestreckt. Der Betrag wird mit der nächsten Auszahlung verrechnet oder beim Eigentümer angefordert.</Hinweis>
      )}
      <LeerZustand icon={Receipt} titel="Buchungen und Auszahlungen je Monat" text={BUCHHALTUNG_FOLGT_LANG} />
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} icon={Check} disabled title={BUCHHALTUNG_FOLGT}>Ausgleich erfassen</ImmoButton>
        <ImmoButton groesse={44} icon={PaperPlaneTilt} disabled title={BUCHHALTUNG_FOLGT}>Auszahlungsavis senden</ImmoButton>
      </div>
    </>
  );
}

// ── WEG-Verwalter (extern) (Screen 29) ─────────────────────────────────────────
export function WegExtPanel() {
  const s = useEigSeite();
  const openKontakt = useOpenKontakt();
  const z = s.daten.zeile;
  const ext = z.zuordnungen.filter(x => x.rolle === 'SEV');
  return (
    <>
      {ext.map(zo => {
        const fv = s.daten.fremdverwalter.find(f => f.objektId === zo.objektId);
        const hg = zo.einheiten.reduce((a, e) => a + e.hausgeld, 0);
        const betreff = encodeURIComponent(`Unterlagen WEG ${zo.objektName} – ${s.name}`);
        const text = encodeURIComponent(`Guten Tag,\n\nwir verwalten das Sondereigentum von ${s.name} (${zo.einheiten.map(e => e.bezeichnung).join(', ')}) in ${zo.objektName}. Bitte senden Sie uns die aktuelle Jahresabrechnung, den Wirtschaftsplan und die Einladungen zur Eigentümerversammlung zu.\n\nMit freundlichen Grüßen`);
        return (
          <React.Fragment key={zo.objektId}>
            <Zeilen titel="Fremdverwalter der WEG" zeilen={[
              { label: 'Verwalter', wert: fv?.name ?? 'nicht hinterlegt' },
              { label: 'Objekt', wert: zo.objektName },
              { label: 'Ansprechpartner', wert: fv?.ansprechpartner || '–' },
              { label: 'Telefon', wert: fv?.telefon || '–' },
              { label: 'E-Mail', wert: fv?.email || '–' },
              { label: 'Hausgeld', wert: `${eur(hg)} / Monat, zahlen wir aus der Miete`, zahl: true },
            ]} />
            {!fv && (
              <Hinweis titel="Fremdverwalter nicht hinterlegt">Am Objekt ist kein Kontakt mit der Funktion „Immobilienverwaltung“ eingetragen. Im Objekt unter Kontakte ergänzen.</Hinweis>
            )}
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              {fv?.email
                ? <a href={`mailto:${fv.email}?subject=${betreff}&body=${text}`} className="immo-hover-flaeche inline-flex items-center"
                  style={{ height: 44, padding: '0 16px', gap: 7, borderRadius: 22, background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  <EnvelopeSimple weight="bold" size={14} />Unterlagen anfordern</a>
                : <ImmoButton groesse={44} icon={EnvelopeSimple} disabled title="Keine E-Mail des Fremdverwalters hinterlegt">Unterlagen anfordern</ImmoButton>}
              <ImmoButton groesse={44} icon={ShareFat} onClick={() => s.oeffnePanel('dokumente')}>An Eigentümer weiterleiten</ImmoButton>
              {fv ? <ImmoButton groesse={44} icon={AddressBook} onClick={() => openKontakt(fv.kontaktId, 'kontakt')}>Kontakt öffnen</ImmoButton>
                : <ImmoButton groesse={44} icon={AddressBook} onClick={() => s.zumObjekt(zo.objektId)}>Objekt öffnen</ImmoButton>}
            </div>
          </React.Fragment>
        );
      })}
      <LeerZustand icon={FileText} titel="Unterlagen vom Fremdverwalter" text="Eingegangene Abrechnungen, Wirtschaftspläne und Einladungen erscheinen unter Dokumente, sobald sie dem Eigentümer zugeordnet sind." />
    </>
  );
}

// ── Verwaltervertrag (Screen 30) ───────────────────────────────────────────────
export function VertragPanel() {
  const s = useEigSeite();
  const navigate = useNavigate();
  const z = s.daten.zeile;
  const w = z.werte;
  const eig = s.daten.eigentuemer;
  const [toast, zeige] = useToastPill();
  const verg = heutigeVerguetung(s);
  const geplant = geplanteVerguetung(s.daten.verguetungen, s.heute);
  const frist = fristAus(eig.kuendigungsfrist_monate, eig.kuendigung_zum);
  const [neu, setNeu] = useState(verg === null ? '' : String(verg).replace('.', ','));
  const [ab, setAb] = useState(() => naechsterMonatsanfang(s.heute));
  const [fristMonate, setFristMonate] = useState(frist.monate);
  const [fristZum, setFristZum] = useState<KuendigungZum>(frist.zum);
  const [kuendigen, setKuendigen] = useState(false);
  const [ende, setEnde] = useState(() => kuendigungsTermin(s.heute, frist.monate, frist.zum));
  const [laeuft, setLaeuft] = useState(false);
  const mandatsende = t(eig.vertragsende).slice(0, 10) || null;
  const vertrag = useMemo(() => s.daten.dokumente.find(d => /verwaltervertrag|verwaltungsvertrag/i.test(`${d.titel ?? ''} ${d.datei_name ?? ''} ${d.kategorie ?? ''}`)), [s.daten.dokumente]);
  const neuZahl = leseZahl(neu);
  const neuFehler = neu.trim() === '' || neuZahl === null || neuZahl < 0;

  const fuehreAus = async (aktion: () => Promise<void>, meldung: string) => {
    setLaeuft(true);
    try {
      await aktion();
      zeige(meldung);
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
      setKuendigen(false);
    }
  };
  const zeigeVertrag = async () => {
    if (!vertrag || !(await oeffneMieterDokument(vertrag))) zeige('Vertrag konnte nicht geöffnet werden', true);
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <Zeilen titel={`Verwaltervertrag mit ${s.name}`} zeilen={[
        { label: 'Art', wert: vertragsArt(w) },
        { label: 'Umfang', wert: umfangText(z.zuordnungen) },
        { label: 'Vertragsbeginn', wert: z.beginn ? deDatum(z.beginn) : '–', zahl: true },
        { label: 'Vertragsende', wert: <span style={{ color: mandatsende || z.endetAm ? 'var(--warn)' : 'var(--ink)' }}>{mandatsende ? deDatum(mandatsende) : z.endetAm ? deDatum(z.endetAm) : 'unbefristet'}</span>, zahl: true },
        { label: 'Laufzeit', wert: vertragsDauer(z.beginn, s.heute) },
        { label: 'Kündigungsfrist', wert: `${fristText(frist)}${frist.standard ? ' (Standard)' : ''}` },
        { label: 'Vergütung je Einheit', wert: verg === null ? 'nicht hinterlegt' : `${eur(verg)} / Monat zzgl. MwSt.`, zahl: true },
        { label: 'Vergütung gesamt', wert: w.vergM === null ? '–' : `${eur(w.vergM)} / Monat`, zahl: true },
      ]} />
      {w.hasWEG && (
        <Hinweis titel="WEG-Verwaltung">Die Verwaltung des Gemeinschaftseigentums läuft getrennt davon über die Bestellung durch die WEG und wird über das Hausgeld vergütet.</Hinweis>
      )}

      <InnenKarte style={GLAS}>
        <KartenTitel>Vergütung anpassen</KartenTitel>
        <FeldRaster minBreite={200}>
          <TextFeld label="Vergütung je Einheit" icon={CurrencyEur} inputMode="decimal" wert={neu} onChange={setNeu} suffix="€" placeholder="0"
            fehler={neu.trim() && neuFehler ? 'Betrag prüfen' : undefined} />
          <TextFeld label="Gültig ab" icon={CalendarBlank} type="date" wert={ab} onChange={setAb} />
        </FeldRaster>
        {s.daten.verguetungen.length > 0 && (
          <div className="flex flex-col" style={{ gap: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Verlauf</span>
            {s.daten.verguetungen.map(v => {
              const zukunft = v.ab > s.heute;
              const aktuell = !zukunft && v.ab === s.daten.verguetungen.find(x => x.ab <= s.heute)?.ab;
              return (
                <div key={v.ab} className="flex items-center" style={{ gap: 4 }}>
                  <div className="flex-1 min-w-0">
                    <PanelZeile icon={CurrencyEur} ton={zukunft ? 'info' : aktuell ? 'ok' : 'neutral'} titel={`${eur(v.betrag)} je Einheit`} sub={`gültig ab ${deDatum(v.ab)}`}
                      chip={zukunft ? { text: 'geplant', ton: 'info' } : aktuell ? { text: 'aktuell', ton: 'ok' } : undefined} />
                  </div>
                  {zukunft && (
                    <ImmoButton groesse={32} icon={X} aria-label={`Geplante Vergütung ab ${deDatum(v.ab)} entfernen`} title="Geplante Änderung entfernen" disabled={laeuft}
                      onClick={() => { void fuehreAus(() => loescheGeplanteVerguetung(eig.id, v.ab, s.heute), 'Geplante Änderung entfernt'); }} />
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || neuFehler || !ab}
            onClick={() => { void fuehreAus(() => speichereVerguetung(eig.id, Math.round((neuZahl ?? 0) * 100) / 100, ab, s.heute), `Vergütung ab ${deDatum(ab)}: ${eur(neuZahl ?? 0)} je Einheit`); }}>
            Speichern
          </ImmoButton>
        </div>
        {geplant && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Ab {deDatum(geplant.ab)} gilt {eur(geplant.betrag)} je Einheit.</span>}
      </InnenKarte>

      <InnenKarte style={GLAS}>
        <KartenTitel>Kündigungsfrist</KartenTitel>
        <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
          <Stepper label="Kündigungsfrist in Monaten" wert={fristMonate} onChange={setFristMonate} min={0} max={24} einheit={fristMonate === 1 ? 'Monat' : 'Monate'} />
          <SegmentedControl<KuendigungZum> ariaLabel="Kündigung zum" hoehe={34} wert={fristZum} onChange={setFristZum} segmente={KUENDIGUNG_ZUM.map(k => ({ key: k.key, label: k.label }))} />
        </div>
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || (!frist.standard && fristMonate === frist.monate && fristZum === frist.zum)}
            onClick={() => { void fuehreAus(() => aktualisiereEigentuemer(eig.id, { kuendigungsfrist_monate: fristMonate, kuendigung_zum: fristZum }), `Kündigungsfrist: ${fristText({ monate: fristMonate, zum: fristZum })}`); }}>
            Frist speichern
          </ImmoButton>
        </div>
      </InnenKarte>

      {kuendigen && (
        <InnenKarte style={{ ...GLAS, boxShadow: 'inset 0 0 0 2px var(--warn)' }}>
          <KartenTitel icon={SignOut}>Mandat kündigen</KartenTitel>
          <span style={{ fontSize: 13, color: 'var(--ink2)' }}>Vorschlag nach der Frist ({fristText(frist)}). Der Eigentümer erscheint danach unter „Wechsel &amp; Mandatsende“.</span>
          <FeldRaster minBreite={200}>
            <TextFeld label="Mandat endet am" type="date" icon={CalendarBlank} wert={ende} onChange={setEnde} min={s.heute} />
          </FeldRaster>
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton onClick={() => setKuendigen(false)}>Abbrechen</ImmoButton>
            <ImmoButton art="akzent" icon={SignOut} disabled={laeuft || !ende}
              onClick={() => { void fuehreAus(() => aktualisiereEigentuemer(eig.id, { vertragsende: ende }), `Mandat endet zum ${deDatum(ende)}`); }}>Kündigung speichern</ImmoButton>
          </div>
        </InnenKarte>
      )}
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {mandatsende && mandatsende >= s.heute
          ? <ImmoButton groesse={44} icon={ArrowCounterClockwise} disabled={laeuft} onClick={() => { void fuehreAus(() => aktualisiereEigentuemer(eig.id, { vertragsende: null }), 'Mandatsende zurückgenommen'); }}>Mandatsende zurücknehmen</ImmoButton>
          : <ImmoButton groesse={44} icon={SignOut} disabled={laeuft} aria-expanded={kuendigen} onClick={() => setKuendigen(k => !k)}>Mandat kündigen</ImmoButton>}
        <ImmoButton groesse={44} icon={FileText} disabled={!vertrag} title={vertrag ? undefined : 'Kein Verwaltervertrag unter Dokumente'} onClick={() => { void zeigeVertrag(); }}>Vertrag anzeigen</ImmoButton>
        <ImmoButton groesse={44} icon={ArrowsLeftRight} onClick={() => navigate('/admin/eigentuemer?neu=1')}>Eigentümerwechsel erfassen</ImmoButton>
      </div>
    </>
  );
}

// ── Freigaben (Screen 31) ──────────────────────────────────────────────────────
const FREIGABE_ANZEIGE: Record<FreigabeStatus, { icon: Icon; ton: 'ok' | 'bad' | 'warn'; chip: string }> = {
  offen: { icon: HourglassMedium, ton: 'warn', chip: 'wartet' },
  freigegeben: { icon: CheckCircle, ton: 'ok', chip: 'freigegeben' },
  abgelehnt: { icon: XCircle, ton: 'bad', chip: 'abgelehnt' },
};

export function FreigabenPanel() {
  const s = useEigSeite();
  const eig = s.daten.eigentuemer;
  const w = s.daten.zeile.werte;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const wertgrenze = zahl(eig.freigabe_wertgrenze);
  const [grenze, setGrenze] = useState(wertgrenze === null ? '' : String(wertgrenze).replace('.', ','));
  const [text, setText] = useState('');
  const [betrag, setBetrag] = useState('');
  const [dienstleister, setDienstleister] = useState('');
  const verwaltete = s.daten.zeile.zuordnungen.filter(x => x.rolle !== 'WEG');
  const [objektId, setObjektId] = useState<string>(verwaltete[0]?.objektId ?? '');
  const k = freigabeKennzahlen(s.daten.freigaben);
  const objekte = new Map(s.daten.objekte.map(o => [o.id, o.bezeichnung ?? 'Objekt']));
  const betragZahl = betrag.trim() ? leseZahl(betrag) : null;
  const grenzeZahl = grenze.trim() ? leseZahl(grenze) : null;

  const fuehreAus = async (aktion: () => Promise<void>, meldung: string) => {
    setLaeuft(true);
    try { await aktion(); zeige(meldung); meldeEigentuemerGeaendert(eig.id); return true; }
    catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); return false; }
    finally { setLaeuft(false); }
  };
  const anfragen = async () => {
    const frei = innerhalbWertgrenze(betragZahl, wertgrenze);
    const ok = await fuehreAus(
      () => erfasseFreigabe(eig.id, { objektId: objektId || null, einheitId: null, beschreibung: text, betrag: betragZahl, dienstleister }, frei),
      frei ? `„${text.trim()}“ liegt innerhalb der Wertgrenze – ohne Rückfrage freigegeben` : `Freigabe „${text.trim()}“ bei ${s.name} angefragt`,
    );
    if (!ok) return;
    // Anfrage per E-Mail (das Portal wird noch angebunden)
    if (!frei && s.email) {
      const betreff = encodeURIComponent(`Bitte um Freigabe: ${text.trim()}`);
      const inhalt = encodeURIComponent(`Guten Tag,\n\nwir bitten um Ihre Freigabe für folgende Maßnahme:\n\n${text.trim()}${objektId ? `\nObjekt: ${objekte.get(objektId) ?? ''}` : ''}${betragZahl !== null ? `\nBetrag: ${eur(betragZahl)}` : ''}${dienstleister.trim() ? `\nAuftragnehmer: ${dienstleister.trim()}` : ''}\n\nBitte geben Sie uns kurz Bescheid.\n\nMit freundlichen Grüßen`);
      window.location.href = `mailto:${s.email}?subject=${betreff}&body=${inhalt}`;
    }
    setText(''); setBetrag(''); setDienstleister('');
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe>
        <KpiTile layout="kachel" label="Offen" wert={k.offen} icon={HourglassMedium} ton="warn" />
        <KpiTile layout="kachel" label="Volumen offen" wert={eur(k.volumen, { nachkomma: 0 })} icon={Coins} ton="acc" />
        <KpiTile layout="kachel" label="Wertgrenze ohne Rückfrage" wert={wertgrenze === null ? '–' : eur(wertgrenze, { nachkomma: 0 })} icon={ShieldCheck} ton="ok" />
      </KpiReihe>
      {w.hasSEV && <Hinweis titel="Hinweis">Freigaben betreffen nur das Sondereigentum. Maßnahmen am Gemeinschaftseigentum beschließt die WEG.</Hinweis>}

      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel rechts={k.offen ? 'antippen = freigegeben · × = abgelehnt' : undefined}>Aufträge &amp; Angebote</KartenTitel></div>
        {s.daten.freigaben.length === 0 && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Freigaben angefragt</span>}
        {s.daten.freigaben.map(f => {
          const a = FREIGABE_ANZEIGE[f.status];
          const sub = [f.objekt_id ? objekte.get(f.objekt_id) : '', f.dienstleister_name ?? '', f.status === 'offen' ? `angefragt ${deDatum(f.angefragt_am.slice(0, 10))}` : f.entschieden_am ? `${a.chip} ${deDatum(f.entschieden_am.slice(0, 10))}` : '', f.notiz ?? '']
            .filter(Boolean).join(' · ');
          return (
            <div key={f.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={a.icon} ton={a.ton} titel={f.beschreibung} sub={sub} rechts={f.betrag === null ? '–' : eur(f.betrag, { nachkomma: 0 })}
                  chip={{ text: a.chip, ton: a.ton }}
                  onClick={f.status === 'offen' && !laeuft ? () => { void fuehreAus(() => entscheideFreigabe(f.id, 'freigegeben'), `${f.beschreibung} freigegeben`); } : undefined}
                  ariaLabel={f.status === 'offen' ? `${f.beschreibung} freigeben` : undefined} />
              </div>
              {f.status === 'offen'
                ? <ImmoButton groesse={32} icon={X} aria-label={`${f.beschreibung} ablehnen`} title="Abgelehnt erfassen" disabled={laeuft}
                  onClick={() => { void fuehreAus(() => entscheideFreigabe(f.id, 'abgelehnt'), `${f.beschreibung} abgelehnt`); }} />
                : <ImmoButton groesse={32} icon={ArrowCounterClockwise} aria-label={`${f.beschreibung} wieder öffnen`} title="Entscheidung zurücknehmen" disabled={laeuft}
                  onClick={() => { void fuehreAus(() => entscheideFreigabe(f.id, 'offen'), `${f.beschreibung} wieder offen`); }} />}
            </div>
          );
        })}
      </InnenKarte>

      <InnenKarte style={GLAS}>
        <KartenTitel>Freigabe anfragen</KartenTitel>
        <FeldRaster minBreite={200}>
          <TextFeld label="Was soll freigegeben werden?" icon={TextAlignLeft} wert={text} onChange={setText} placeholder="z. B. Heizungswartung" voll />
          <TextFeld label="Betrag" icon={CurrencyEur} inputMode="decimal" wert={betrag} onChange={setBetrag} suffix="€" placeholder="0"
            fehler={betrag.trim() && (betragZahl === null || betragZahl < 0) ? 'Betrag prüfen' : undefined} />
          <TextFeld label="Auftragnehmer" icon={Wrench} wert={dienstleister} onChange={setDienstleister} placeholder="z. B. Glas Becker" />
        </FeldRaster>
        {verwaltete.length > 1 && (
          <SegmentedControl<string> ariaLabel="Objekt" hoehe={34} className="self-start" wert={objektId} onChange={setObjektId}
            segmente={verwaltete.map(x => ({ key: x.objektId, label: x.objektName }))} />
        )}
        {innerhalbWertgrenze(betragZahl, wertgrenze) && <span style={{ fontSize: 12, color: 'var(--ok)' }}>Innerhalb der Wertgrenze – wird ohne Rückfrage freigegeben.</span>}
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={PaperPlaneTilt} disabled={laeuft || !text.trim() || (betrag.trim() !== '' && (betragZahl === null || betragZahl < 0))}
            onClick={() => { void anfragen(); }}>
            {innerhalbWertgrenze(betragZahl, wertgrenze) ? 'Freigabe erfassen' : s.email ? 'Per E-Mail anfragen' : 'Anfrage erfassen'}
          </ImmoButton>
        </div>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Die Anfrage im Eigentümerportal wird noch angebunden; die Entscheidung des Eigentümers hier erfassen.</span>
      </InnenKarte>

      <InnenKarte style={GLAS}>
        <KartenTitel>Wertgrenze</KartenTitel>
        <div className="flex items-end flex-wrap" style={{ gap: 8 }}>
          <div style={{ flex: '1 1 220px' }}>
            <TextFeld label="Aufträge bis zu diesem Betrag ohne Rückfrage" icon={ShieldCheck} inputMode="decimal" wert={grenze} onChange={setGrenze} suffix="€" placeholder="leer = immer fragen"
              fehler={grenze.trim() && (grenzeZahl === null || grenzeZahl < 0) ? 'Betrag prüfen' : undefined} />
          </div>
          <ImmoButton art="akzent" groesse={48} icon={Check} disabled={laeuft || (grenze.trim() !== '' && (grenzeZahl === null || grenzeZahl < 0)) || grenzeZahl === wertgrenze}
            onClick={() => { void fuehreAus(() => aktualisiereEigentuemer(eig.id, { freigabe_wertgrenze: grenzeZahl === null ? null : Math.round(grenzeZahl * 100) / 100 }), grenzeZahl === null ? 'Wertgrenze entfernt – jede Maßnahme braucht eine Freigabe' : `Wertgrenze: ${eur(grenzeZahl)}`); }}>
            Speichern
          </ImmoButton>
        </div>
      </InnenKarte>

      <AeltereEinsaetzeKarte eigentuemerId={eig.id} />

      {/* #564 T5: E-Mail bei Neuigkeiten im Eigentümerportal – Standard aus; verschickt wird erst, wenn die
          Benachrichtigungen auf der Eigentümer-Liste eingeschaltet sind und der Testmodus aus ist. */}
      <InnenKarte style={GLAS}>
        <KartenTitel>E-Mail aus dem Eigentümerportal</KartenTitel>
        <SegmentedControl<'aus' | 'sofort' | 'taeglich'> ariaLabel="E-Mail-Benachrichtigung" hoehe={34} className="self-start"
          wert={(eig.portal_benachrichtigung as 'aus' | 'sofort' | 'taeglich' | undefined) ?? 'aus'}
          onChange={(neu) => { if (!laeuft) void fuehreAus(() => aktualisiereEigentuemer(eig.id, { portal_benachrichtigung: neu }),
            neu === 'aus' ? 'Keine E-Mails aus dem Portal' : neu === 'sofort' ? 'E-Mail bei jeder Neuigkeit' : 'Eine E-Mail am Tag (7 Uhr)'); }}
          segmente={[{ key: 'aus', label: 'Aus' }, { key: 'sofort', label: 'Sofort' }, { key: 'taeglich', label: 'Täglich' }]} />
        <span style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--ink3)' }}>
          Bei neuem Stand eines Vorgangs, einer Antwort im Portal, Kosten oder Rechnungen, die auf seine Freigabe warten. Die Mail enthält nur kurze Hinweise und den Link ins Portal.
          Verschickt wird erst, wenn auf der Eigentümer-Liste „E-Mail-Benachrichtigung“ eingeschaltet und der Testmodus aus ist.
        </span>
      </InnenKarte>
    </>
  );
}

// ── Steuer (Screen 32) ─────────────────────────────────────────────────────────
export function SteuerPanel() {
  const s = useEigSeite();
  const navigate = useNavigate();
  const z = s.daten.zeile;
  const eig = s.daten.eigentuemer;
  const [toast, zeige] = useToastPill();
  const [finanzamt, setFinanzamt] = useState(t(eig.finanzamt));
  const [laeuft, setLaeuft] = useState(false);
  const anlageV = anlageVZeilen(z.zuordnungen);
  const sid = t(eig.steuer_id).replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4');
  const speichere = async () => {
    setLaeuft(true);
    try {
      await aktualisiereEigentuemer(eig.id, { finanzamt: finanzamt.trim() || null });
      zeige('Finanzamt gespeichert');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <Zeilen titel="Steuerdaten" zeilen={[
        { label: 'Steuer-ID', wert: sid || '–', zahl: true },
        { label: 'Steuer-Nr.', wert: t(eig.steuer_nr) || '–', zahl: true },
        { label: 'Rechtsform', wert: z.firma ? 'Gesellschaft' : 'Privatperson' },
        { label: 'Finanzamt', wert: t(eig.finanzamt) || 'nicht hinterlegt' },
      ]} />
      <InnenKarte style={GLAS}>
        <div className="flex items-end flex-wrap" style={{ gap: 8 }}>
          <div style={{ flex: '1 1 260px' }}>
            <TextFeld label="Finanzamt" icon={Bank} wert={finanzamt} onChange={setFinanzamt} placeholder="z. B. Marburg-Biedenkopf" />
          </div>
          <ImmoButton art="akzent" groesse={48} icon={Check} disabled={laeuft || finanzamt.trim() === t(eig.finanzamt)} onClick={() => { void speichere(); }}>Speichern</ImmoButton>
        </div>
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Steuer-ID und Steuer-Nr. werden in den Stammdaten gepflegt.</span>
      </InnenKarte>
      <SteuerpaketEigentuemer eigentuemerId={eig.id} name={z.name} zuordnungen={z.zuordnungen} heute={s.heute}
        onObjekt={(id) => navigate(`/admin/objekte/${encodeURIComponent(id)}?panel=finanzen`)} />
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton groesse={44} icon={Export} disabled title={BUCHHALTUNG_FOLGT}>DATEV-Export</ImmoButton>
      </div>
    </>
  );
}

// ── Eigentümerabrechnung (Screen 33) ───────────────────────────────────────────
export function AbrechnungPanel() {
  const s = useEigSeite();
  const jahr = Number(s.heute.slice(0, 4));
  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" label="Letztes Jahr" wert="–" icon={CalendarCheck} ton="acc" />
        <KpiTile layout="kachel" label="Überschuss nach Auszahlungen" wert="–" icon={Scales} ton="ok" />
      </KpiReihe>
      <LeerZustand icon={Receipt} titel="Eigentümerabrechnungen" text={`${BUCHHALTUNG_FOLGT_LANG}${s.daten.zeile.werte.hasSEV ? ' Bei SEV enthält die Abrechnung die WEG-Einzelabrechnung.' : ''}`}>
        <ImmoButton icon={Briefcase} disabled title={BUCHHALTUNG_FOLGT} style={{ marginTop: 6 }}>{`Abrechnung ${jahr} vorbereiten`}</ImmoButton>
      </LeerZustand>
    </>
  );
}

// ── Mieter (Screen 58) ─────────────────────────────────────────────────────────
export function MieterPanel() {
  const s = useEigSeite();
  const openKontakt = useOpenKontakt();
  const z = s.daten.zeile;
  const p = s.portfolio;
  const je = useMemo(() => zuordnungsDaten(s.daten.objekte, s.daten.einheiten, s.daten.mieter, s.heute).mieterJeEinheit, [s.daten, s.heute]);
  const zeilen = mieterZeilen(z.zuordnungen, je, s.heute);
  const kalt = zeilen.reduce((a, m) => a + m.kalt, 0);
  return (
    <>
      <KpiReihe>
        <KpiTile layout="kachel" label="Mieter (von uns verwaltet)" wert={zeilen.length} icon={Users} ton="acc" />
        <KpiTile layout="kachel" label="Leerstand" wert={p.verwaltet - p.vermietet} icon={DoorOpen} ton="warn" />
        <KpiTile layout="kachel" label="Kaltmiete / Monat" wert={eur(kalt, { nachkomma: 0 })} icon={Coins} ton="ok" />
      </KpiReihe>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Mieter in den verwalteten Einheiten</KartenTitel></div>
        {zeilen.length === 0 && <span style={{ padding: '6px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Mieter in den verwalteten Einheiten.</span>}
        {zeilen.map(m => (
          <PanelZeile key={`${m.id}|${m.einheitId}`} icon={User} ton="acc" titel={m.name} sub={m.ort} rechts={m.kalt ? eur(m.kalt, { nachkomma: 0 }) : '–'}
            onClick={() => openKontakt(m.id, 'mieter')} ariaLabel={`${m.name} öffnen`} />
        ))}
      </InnenKarte>
    </>
  );
}

